import { Check, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell, PageHero } from "@/components/knmc/PageShell";
import type { HealthPackage } from "@/hooks/usePackages";
import { useState } from "react";

function formatPrice(price: number | null): string {
  if (price === null || price === undefined) return "Not Disclosed";
  return `NPR ${Number(price).toLocaleString("en-IN")}`;
}

// PackageCard component with expand/collapse
function PackageCard({ p }: { p: HealthPackage }) {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? p.items : p.items.slice(0, 6);
  const hasMore = p.items.length > 6;

  return (
    <div
      className={`hover-lift relative flex h-full flex-col rounded-3xl p-8 transition-all duration-300 shadow-xl ${
        p.most_popular
          ? "bg-[#F7F4EA] border border-amber-200/60 text-stone-900"
          : "bg-card border border-border text-foreground"
      }`}
    >
      {/* Most Popular Badge */}
      {p.most_popular && (
        <span className="absolute -top-3.5 left-6 flex items-center gap-1.5 rounded-full border border-amber-300/80 bg-gradient-to-r from-[#E7D6B1] to-[#C9A66B] px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[#3D280A] shadow-md">
          <Sparkles className="h-3.5 w-3.5 fill-current" />
          Most Popular
        </span>
      )}

      <div className="flex h-full flex-col">
        {/* Card Title & Description */}
        <h3
          className={`font-serif text-3xl font-bold tracking-tight ${
            p.most_popular ? "text-[#A0672A]" : "text-foreground"
          }`}
        >
          {p.name}
        </h3>
        {p.description && (
          <p
            className={`mt-2 text-sm leading-relaxed ${
              p.most_popular ? "text-stone-700" : "text-muted-foreground"
            }`}
          >
            {p.description}
          </p>
        )}

        {/* Price Block */}
        <div
          className={`mt-6 rounded-2xl p-4 shadow-sm transition-colors ${
            p.most_popular
              ? "bg-[#094D41] text-white"
              : "bg-muted/40 border border-border text-foreground"
          }`}
        >
          <div className="flex items-end gap-2">
            <span className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              {formatPrice(p.price)}
            </span>
            <span
              className={`pb-1 text-sm font-normal ${
                p.most_popular ? "text-emerald-100/80" : "text-muted-foreground"
              }`}
            >
              / package
            </span>
          </div>
        </div>

        {/* Included Tests List */}
        <div className="mt-6 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-semibold ${
                p.most_popular ? "text-stone-900" : "text-foreground"
              }`}
            >
              Package Includes
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                p.most_popular
                  ? "bg-[#094D41] text-white"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {p.items.length} Tests
            </span>
          </div>

          <ul className="mt-4 space-y-3">
            {visibleItems.map((i) => (
              <li
                key={i.item_code}
                className={`flex items-start gap-2.5 text-sm font-medium ${
                  p.most_popular ? "text-stone-800" : "text-muted-foreground"
                }`}
              >
                <Check
                  className={`mt-0.5 h-4 w-4 shrink-0 stroke-[2.5] ${
                    p.most_popular ? "text-stone-900" : "text-primary"
                  }`}
                />
                <span>{i.description}</span>
              </li>
            ))}
          </ul>

          {hasMore && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className={`mt-5 inline-flex items-center gap-1.5 text-sm font-bold underline underline-offset-4 transition-colors ${
                p.most_popular
                  ? "text-stone-900 hover:text-[#094D41]"
                  : "text-primary hover:text-primary/80"
              }`}
            >
              {expanded ? (
                <>
                  Show Less
                  <ChevronUp className="h-4 w-4" />
                </>
              ) : (
                <>
                  +{p.items.length - 6} more tests
                  <ChevronDown className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>

        {/* CTA Button */}
        <Button
          className={`mt-8 w-full rounded-full py-6 text-sm font-bold uppercase tracking-wider shadow-md transition-all ${
            p.most_popular
              ? "bg-[#094D41] text-white hover:bg-[#06382F]"
              : "bg-primary text-primary-foreground hover:bg-primary/90"
          }`}
          onClick={() => {
            // Add your booking logic here
            console.log(`Booking package: ${p.name}`);
          }}
        >
          Book Package
        </Button>
      </div>
    </div>
  );
}

export default function HealthPackages({ packages }: { packages: HealthPackage[] }) {
  return (
    <PageShell>
      <PageHero
        eyebrow="Health Packages"
        title="Preventive care that pays for itself"
        description="Book online and visit any day — reports delivered to your patient portal within 48 hours."
      />
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          {packages.length === 0 ? (
            <p className="py-32 text-center text-muted-foreground">
              No packages available at the moment.
            </p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {packages.map((p) => (
                <PackageCard key={p.name} p={p} />
              ))}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}